/**
 * A markdown renderer for the legal documents, and nothing else.
 *
 * Supported, because it is all the agreement and the guides use:
 *   headings (# through ####), paragraphs, unordered and ordered lists,
 *   blockquotes, horizontal rules, pipe tables, and inline **bold**,
 *   *italic*, `code` and [links](/path).
 *
 * NOT supported: images, nested lists, HTML, footnotes. A backend test
 * (test_content.py) fails if a document starts using something this cannot
 * show, so an editor finds out at CI rather than by looking at a mangled page.
 *
 * Output is React elements built from text, never `dangerouslySetInnerHTML`,
 * so no document content can become markup we did not intend.
 */

import { Fragment, type ReactNode } from "react";

import { Link } from "../router";

const HEADING = /^(#{1,4})\s+(.*)$/;
const UNORDERED = /^[-*]\s+(.*)$/;
const ORDERED = /^(\d+)\.\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^(-{3,}|\*{3,}|_{3,})$/;

// Ordered so that ** is tried before *, and links before anything inside them.
const INLINE = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

export function Markdown({ source }: { source: string }) {
  return <div className="prose">{renderBlocks(source)}</div>;
}

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; lines: string[] }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "quote"; lines: string[] }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "rule" };

const TABLE_ROW = /^\|(.+)\|\s*$/;
// | --- | :--: | ---: |  — the separator that makes the line above a header.
const TABLE_DIVIDER = /^\|[\s|:-]+\|\s*$/;

function tableCells(line: string): string[] {
  const inner = TABLE_ROW.exec(line.trim());
  if (!inner) return [];
  return inner[1]!.split("|").map((cell) => cell.trim());
}

function parse(source: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;

  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  const lines = source.split("\n");

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!.trimEnd();

    if (!line.trim()) {
      flush();
      continue;
    }

    // A table is only a table if the next line is the divider — otherwise a
    // paragraph that happens to contain pipes would be swallowed.
    const next = lines[index + 1]?.trim() ?? "";
    if (TABLE_ROW.test(line.trim()) && TABLE_DIVIDER.test(next)) {
      flush();
      const header = tableCells(line);
      const rows: string[][] = [];
      index += 2; // past the header and the divider
      while (index < lines.length && TABLE_ROW.test(lines[index]!.trim())) {
        rows.push(tableCells(lines[index]!));
        index += 1;
      }
      index -= 1; // the outer loop increments
      blocks.push({ kind: "table", header, rows });
      continue;
    }

    if (RULE.test(line.trim())) {
      flush();
      blocks.push({ kind: "rule" });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({
        kind: "heading",
        level: heading[1]!.length,
        text: heading[2]!,
      });
      continue;
    }

    const quote = QUOTE.exec(line);
    if (quote) {
      if (current?.kind !== "quote") {
        flush();
        current = { kind: "quote", lines: [] };
      }
      current.lines.push(quote[1]!);
      continue;
    }

    const unordered = UNORDERED.exec(line);
    const ordered = ORDERED.exec(line);
    if (unordered || ordered) {
      const wantsOrdered = Boolean(ordered);
      if (current?.kind !== "list" || current.ordered !== wantsOrdered) {
        flush();
        current = { kind: "list", ordered: wantsOrdered, items: [] };
      }
      current.items.push((unordered ? unordered[1] : ordered![2])!);
      continue;
    }

    // A plain line continues whatever is open — list items and paragraphs in
    // these documents wrap across several source lines.
    if (current?.kind === "list") {
      current.items[current.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    if (current?.kind === "paragraph") {
      current.lines.push(line.trim());
      continue;
    }
    flush();
    current = { kind: "paragraph", lines: [line.trim()] };
  }

  flush();
  return blocks;
}

function renderBlocks(source: string): ReactNode[] {
  return parse(source).map((block, index) => {
    switch (block.kind) {
      case "rule":
        return <hr className="rule" key={index} />;

      case "heading": {
        const Tag = `h${Math.min(block.level, 4)}` as "h1" | "h2" | "h3" | "h4";
        return <Tag key={index}>{inline(block.text)}</Tag>;
      }

      case "quote":
        return (
          <blockquote key={index}>{inline(block.lines.join(" "))}</blockquote>
        );

      case "table":
        return (
          // Wide tables scroll inside their own container rather than making
          // the whole page scroll sideways on a phone.
          <div className="table-scroll" key={index}>
            <table>
              <thead>
                <tr>
                  {block.header.map((cell, i) => (
                    <th key={i}>{inline(cell)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c}>{inline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case "list":
        return block.ordered ? (
          <ol key={index}>
            {block.items.map((item, i) => (
              <li key={i}>{inline(item)}</li>
            ))}
          </ol>
        ) : (
          <ul key={index}>
            {block.items.map((item, i) => (
              <li key={i}>{inline(item)}</li>
            ))}
          </ul>
        );

      case "paragraph":
        return <p key={index}>{inline(block.lines.join(" "))}</p>;
    }
  });
}

function inline(text: string): ReactNode[] {
  return text.split(INLINE).filter(Boolean).map((token, index) => {
    const key = index;

    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={key}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith("`") && token.endsWith("`")) {
      return <code key={key}>{token.slice(1, -1)}</code>;
    }
    if (token.startsWith("*") && token.endsWith("*")) {
      return <em key={key}>{token.slice(1, -1)}</em>;
    }

    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
    if (link) {
      const [, label, href] = link as unknown as [string, string, string];
      // Cross-references between documents stay inside the app; anything
      // external opens normally.
      return href.startsWith("/") ? (
        <Link to={href} key={key}>
          {label}
        </Link>
      ) : (
        <a href={href} key={key} target="_blank" rel="noreferrer">
          {label}
        </a>
      );
    }

    return <Fragment key={key}>{token}</Fragment>;
  });
}
